import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AchieverCard from "@/components/AchieverCard";
import { Star, Users, Lock, CheckCircle2, Loader2, Award } from "lucide-react";

export default function StarClubNew() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [cardTier, setCardTier] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/me/star-club-new");
      setData(r.data);
      if (r.data?.current_club > 0) setCardTier(r.data.current_club);
    } catch (err) {
      console.warn("Star Club load failed:", err?.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="flex items-center gap-2 text-neutral-500"><Loader2 className="animate-spin" size={16} /> Loading Star Club…</div>;
  }
  if (!data) return <div className="text-neutral-500">Could not load Star Club.</div>;

  const {
    tiers = [], active_directs = 0, entry_directs = 12, current_club = 0,
    pending_advance = null, pending_need_directs = null, eligible = false,
    waiting = [], history = [], total_earned = 0, matrix_width = 5,
  } = data;

  const slotsFor = (club) => {
    const w = waiting.find((q) => q.club === club);
    return w ? Number(w.slots_filled || 0) : null;
  };

  return (
    <div className="space-y-8" data-testid="starclub-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline flex items-center gap-2"><Star size={14} className="text-[#D4AF37]" /> Star Club</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">Star Club</h1>
          <p className="text-sm text-neutral-400 mt-2 max-w-xl">
            An 8-tier global Matrix-5. Enter with {entry_directs} active direct members; each tier completes
            when {matrix_width} members fill below your global position.
          </p>
        </div>
        <div className="lt-card px-5 py-4 text-right">
          <div className="overline">Total earned</div>
          <div className="font-display text-3xl gold-text tabular mt-1" data-testid="starclub-total-earned">${Number(total_earned || 0).toFixed(2)}</div>
        </div>
      </div>

      {/* Eligibility */}
      <div className="lt-card p-6" data-testid="starclub-eligibility">
        <div className="overline mb-3 flex items-center gap-2"><Users size={14} className="text-[#D4AF37]" /> Your eligibility</div>
        <div className="flex items-end gap-6 flex-wrap">
          <div>
            <div className="text-xs text-neutral-500">Active direct members</div>
            <div className="font-display text-3xl tabular mt-1" data-testid="starclub-active-directs">{active_directs}</div>
          </div>
          <div>
            <div className="text-xs text-neutral-500">Current tier</div>
            <div className="font-display text-3xl tabular mt-1 gold-text">{current_club > 0 ? `Star Club ${current_club}` : "—"}</div>
          </div>
        </div>
        {!eligible && current_club === 0 && (
          <div className="text-sm text-amber-400 mt-4" data-testid="starclub-need-entry">
            Add {Math.max(0, entry_directs - active_directs)} more active direct member(s) to enter Star Club 1.
          </div>
        )}
        {pending_advance && (
          <div className="text-sm text-amber-400 mt-4" data-testid="starclub-need-advance">
            To unlock <span className="gold-text">Star Club {pending_advance}</span>, add
            {" "}{Math.max(0, (pending_need_directs || 0) - active_directs)} more active direct member(s)
            (need {pending_need_directs} total).
          </div>
        )}
      </div>

      {/* Achiever Card — shown once the member has entered Star Club */}
      {current_club > 0 && (
        <div className="lt-card p-6" data-testid="achiever-section">
          <div className="overline mb-1 flex items-center gap-2"><Award size={14} className="text-[#D4AF37]" /> Your Achiever Card</div>
          <p className="text-sm text-neutral-400 mb-4">Upload your photo and download a shareable card celebrating your Star Club achievement.</p>
          <div className="mb-4 max-w-xs">
            <label className="text-xs text-neutral-500">Celebrate tier</label>
            <select
              className="w-full mt-1 bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2 text-sm"
              value={cardTier}
              onChange={(e) => setCardTier(Number(e.target.value))}
              data-testid="achiever-tier-select"
            >
              {Array.from({ length: current_club }, (_, i) => i + 1).map((c) => (
                <option key={c} value={c}>Star Club {c}</option>
              ))}
            </select>
          </div>
          <AchieverCard
            name={user?.full_name}
            userId={user?.user_id}
            club={cardTier}
          />
        </div>
      )}

      {/* Tiers */}
      <div className="lt-card p-6">
        <div className="overline mb-4">The 8 tiers</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {tiers.map((t) => {
            const completed = current_club > t.club || (history.some((h) => h.club === t.club && h.paid_out));
            const isCurrent = current_club === t.club;
            const filled = slotsFor(t.club);
            const locked = !completed && !isCurrent;
            return (
              <div
                key={t.club}
                data-testid={`starclub-tier-${t.club}`}
                className={`rounded-lg border p-4 ${isCurrent ? "border-[#D4AF37]/60 bg-[#D4AF37]/5" : completed ? "border-emerald-600/40 bg-emerald-600/5" : "border-neutral-800 bg-neutral-900/40"}`}
              >
                <div className="flex items-center justify-between">
                  <div className="overline">Star Club {t.club}</div>
                  {completed ? <CheckCircle2 size={16} className="text-emerald-400" />
                    : isCurrent ? <Loader2 size={16} className="text-[#D4AF37]" />
                    : <Lock size={14} className="text-neutral-600" />}
                </div>
                <div className="font-display text-2xl gold-text tabular mt-2">${t.payout.toLocaleString()}</div>
                <div className="text-xs text-neutral-500 mt-2">Needs {t.directs} active directs</div>
                {isCurrent && filled !== null && (
                  <div className="text-xs mt-2 text-[#D4AF37]">Slots filled: {filled}/{matrix_width}</div>
                )}
                <div className="text-[11px] uppercase tracking-widest mt-2 text-neutral-500">
                  {completed ? "Completed" : isCurrent ? "In progress" : "Locked"}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* History */}
      <div className="lt-card p-6">
        <div className="overline mb-3">Payout history</div>
        {history.length === 0 ? (
          <div className="text-sm text-neutral-500">No Star Club payouts yet.</div>
        ) : (
          <table className="lt-table">
            <thead><tr><th>When</th><th>Tier</th><th>Payout</th><th>Status</th></tr></thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id} data-testid={`starclub-history-${h.id}`}>
                  <td className="text-neutral-400">{new Date(h.created_at).toLocaleString()}</td>
                  <td>Star Club {h.club}</td>
                  <td className="tabular gold-text">${Number(h.payout).toLocaleString()}</td>
                  <td className={h.paid_out ? "text-emerald-400" : "text-red-400"}>{h.paid_out ? "Paid" : "Forfeited (no active Play Card)"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
